import { AxiosInstance, AxiosError, AxiosResponseHeaders, RawAxiosResponseHeaders } from 'axios'

import { AxiosErrorResponseData } from '@marketplace-web/core-api/api-client-util'
import { logMessage } from '@marketplace-web/observability/logging-util'

import {
  DD_RESPONSE_DISPLAYED_EVENT,
  DD_RESPONSE_ERROR_EVENT,
  DD_RESPONSE_UNLOAD_EVENT,
} from '../constants/events'
import { isAxiosErrorBlockedByDataDome } from '../utils/utils'
import {
  incrementDataDomeRequestBlocked,
  incrementDataDomeRequestHandled,
  incrementDataDomeScriptMissing,
  dataDomeResponseDisplayedDurationTimer,
  dataDomeResponseErrorDurationTimer,
} from '../utils/observability'

const SCRIPT_FAILS_TO_TRIGGER_TIMEOUT = 15_000

const handleDataDomeCaptchaFailedToRender = ({
  eventAbortController,
  headers,
  url,
  reason,
}: {
  eventAbortController: AbortController
  headers: RawAxiosResponseHeaders | AxiosResponseHeaders | undefined
  url: string
  reason: string
}) => {
  incrementDataDomeScriptMissing({ reason, url })

  logMessage('DataDome captcha failed to render', {
    feature: 'data-dome-script-detection',
    extra: JSON.stringify({
      blockedRequestId: headers?.['x-request-id'],
      blockedDataDomeId: headers?.['x-datadome-cid'],
      blockedUrl: url,
      reason,
    }),
  })

  eventAbortController.abort()
}

const handleDataDomeCaptchaRendered = ({
  timeoutId,
  event,
  eventAbortController,
}: {
  timeoutId: NodeJS.Timeout
  event: string
  eventAbortController: AbortController
}) => {
  clearTimeout(timeoutId)
  incrementDataDomeRequestHandled({ event })
  eventAbortController.abort()
}

export const missingDataDomeScriptDetectionInterceptor = (instance: AxiosInstance) => {
  instance.interceptors.response.use(
    undefined,
    (error: AxiosError<AxiosErrorResponseData<unknown>>) => {
      if (
        error.response &&
        isAxiosErrorBlockedByDataDome(error.response.headers, error.response.data)
      ) {
        incrementDataDomeRequestBlocked()

        const eventAbortController = new AbortController()

        const commonTrackingData = {
          headers: error.response?.headers,
          url: `${error.response?.config?.baseURL}${error.response?.config?.url}`,
        }
        const isScriptInitialized = 'dataDomeOptions' in window

        if (!isScriptInitialized) {
          handleDataDomeCaptchaFailedToRender({
            eventAbortController,
            ...commonTrackingData,
            reason: 'script_missing',
          })

          return Promise.reject(error)
        }

        const stopResponseDisplayedTimer = dataDomeResponseDisplayedDurationTimer()
        const stopResponseErrorTimer = dataDomeResponseErrorDurationTimer()

        const timeoutId = setTimeout(() => {
          handleDataDomeCaptchaFailedToRender({
            eventAbortController,
            ...commonTrackingData,
            reason: `timeout_${SCRIPT_FAILS_TO_TRIGGER_TIMEOUT / 1000}s`,
          })
        }, SCRIPT_FAILS_TO_TRIGGER_TIMEOUT)

        window.addEventListener(
          DD_RESPONSE_DISPLAYED_EVENT,
          () => {
            handleDataDomeCaptchaRendered({
              timeoutId,
              event: DD_RESPONSE_DISPLAYED_EVENT,
              eventAbortController,
            })
            stopResponseDisplayedTimer()
          },
          {
            signal: eventAbortController.signal,
            once: true,
          },
        )

        window.addEventListener(
          DD_RESPONSE_UNLOAD_EVENT,
          () => {
            handleDataDomeCaptchaRendered({
              timeoutId,
              event: DD_RESPONSE_UNLOAD_EVENT,
              eventAbortController,
            })
          },
          {
            signal: eventAbortController.signal,
            once: true,
          },
        )

        window.addEventListener(
          DD_RESPONSE_ERROR_EVENT,
          () => {
            clearTimeout(timeoutId)
            handleDataDomeCaptchaFailedToRender({
              eventAbortController,
              ...commonTrackingData,
              reason: DD_RESPONSE_ERROR_EVENT,
            })
            stopResponseErrorTimer()
          },
          {
            signal: eventAbortController.signal,
            once: true,
          },
        )

        window.addEventListener(
          'beforeunload',
          () => {
            clearTimeout(timeoutId)
            handleDataDomeCaptchaFailedToRender({
              eventAbortController,
              ...commonTrackingData,
              reason: 'beforeunload',
            })
          },
          {
            signal: eventAbortController.signal,
            once: true,
          },
        )
      }

      return Promise.reject(error)
    },
  )
}
